import useGuardedRealtimeList from '../../realtime/useGuardedRealtimeList';
import React, { useState, useEffect } from 'react';
import { Modal, Spinner, Alert } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import { selectApiUrls } from '../../redux/slices/apiUrlSlice';
import { fetchData } from '../../redux/slices/apiCallSlice';
import PropTypes from 'prop-types';

/**
 * InfoBubbleModal - Reusable component for displaying information bubbles
 * 
 * @param {boolean} show - Controls modal visibility
 * @param {function} onHide - Callback when modal is closed
 * @param {string} sectionName - Section name to filter bubbles (e.g., "BookingLog")
 * @param {string} value - Value to filter bubbles (e.g., "Page", "Field")
 * @param {string} title - Optional custom title for the modal
 * @param {boolean} displayAsOrderedList - If true, displays items as an ordered list
 */
const InfoBubbleModal = ({ 
  show, 
  onHide, 
  sectionName, 
  value, 
  title = null,
  displayAsOrderedList = false,
  description = null
}) => {
  const dispatch = useDispatch();
  const apiUrls = useSelector(selectApiUrls);
  
  const [infoBubbles, setInfoBubbles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useGuardedRealtimeList({ apiKey: 'iconData', enabled: Boolean(show && description == null && sectionName && value),
    params: { sectionName, value, isActive: true }, blockDialogs: false, blocked: loading,
    apply: rows => setInfoBubbles([...rows].sort((a, b) => (a.SortOrder || 0) - (b.SortOrder || 0))),
  });

  const fetchInfoBubbles = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Build URL with query parameters
      const url = `${apiUrls.iconData}?sectionName=${encodeURIComponent(sectionName)}&value=${encodeURIComponent(value)}&isActive=true`;
      
      const data = await dispatch(fetchData({ url })).unwrap();
      
      // Create a new array and sort by SortOrder (API should return filtered data)
      const sorted = [...(Array.isArray(data) ? data : [])]
        .sort((a, b) => (a.SortOrder || 0) - (b.SortOrder || 0));
      
      setInfoBubbles(sorted);
      setLoading(false);
    } catch (err) {
      setError(`Failed to load information: ${err.message || err}`);
      setLoading(false);
    }
  };

  // Fetch info bubbles when modal opens
  useEffect(() => {
    if (show && description == null && sectionName && value) {
      fetchInfoBubbles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, sectionName, value, description]);

  const modalTitle = title || `${sectionName} - ${value}`;

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-info-circle me-2"></i>
          {modalTitle}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {description != null && <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{description}</p>}
        {description == null && loading && (
          <div className="text-center py-4">
            <Spinner animation="border" />
            <p className="text-muted mt-2">Loading information...</p>
          </div>
        )}
        
        {description == null && error && (
          <Alert variant="danger">
            {error}
          </Alert>
        )}
        
        {description == null && !loading && !error && infoBubbles.length === 0 && (
          <Alert variant="info">
            No information available for this section.
          </Alert>
        )}
        
        {description == null && !loading && !error && infoBubbles.length > 0 && (
          <>
            {displayAsOrderedList ? (
              <ol>
                {infoBubbles.map((bubble, index) => (
                  <li key={bubble.ID || index} className="mb-2" style={{ whiteSpace: 'pre-wrap' }}>
                    {bubble.DisplayText}
                  </li>
                ))}
              </ol>
            ) : (
              <div>
                {infoBubbles.map((bubble, index) => (
                  <div key={bubble.ID || index} className="mb-3">
                    <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                      {bubble.DisplayText}
                    </p>
                    {index < infoBubbles.length - 1 && <hr className="my-3" />}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

InfoBubbleModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  sectionName: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  title: PropTypes.string,
  displayAsOrderedList: PropTypes.bool,
  description: PropTypes.string
};

export default InfoBubbleModal;
